import { useContext, useEffect, useRef } from 'react';
import GameContext from 'contexts/GameContext';
import { useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';

import { useMaxRunCount } from './maxRunCount';
import { isInitiatorSeat, offeredRunCounts, parseOffer } from './multiRunPhase';
import {
  MultiRunVoteContest,
  MultiRunVoteRole,
  reportMultiRunVoteEventOnce
} from './multiRunVoteAnalytics';
import { isContestingPot, isVoteOpen, readVotePhase } from './votePresentation';
import { voteWindowSecs } from './voteWindow';

/**
 * A window that closes within this much of its deadline ran out; one that closes
 * earlier was closed by the table (a decline, or the initiator's 1X). Covers the
 * frame latency between the server's deadline and the frame that reports it.
 */
export const VOTE_TIMEOUT_TOLERANCE_MS = 500;

type HandFacts = ReturnType<typeof handFacts>;

type Prompt = {
  key: string;
  role: MultiRunVoteRole;
  /** The hand as it stood when this seat was asked. */
  facts: HandFacts;
  offeredMax: number;
  shownAt: number;
  /** Wall-clock deadline, re-anchored on every frame that moves the clock. */
  endAt: number;
  anchor: string;
  cast: boolean;
};

type HandWatch = { key: string; offered: number; facts: HandFacts };

type Game = Record<string, any> | undefined;

/** Wall-clock end of the open window, derived the way useVoteCountdown does. */
const windowEndAt = (game: Game, now: number): number => {
  const elapsed = +game?.time - +game?.lastmove;
  const serverElapsed = Number.isFinite(elapsed) ? Math.max(elapsed, 0) : 0;
  return now + Math.max(voteWindowSecs(game) - serverElapsed, 0) * 1000;
};

const handFacts = (game: Game, players: Record<string, unknown> | undefined) => {
  const contestingSeats = Object.keys(players ?? {})
    .map(Number)
    .filter((seat) => Number.isInteger(seat) && seat > 0 && isContestingPot(game, seat)).length;
  const contest: MultiRunVoteContest = contestingSeats > 2 ? 'multi-way' : 'heads-up';

  return { gameType: String(game?.gameType ?? ''), contest, contestingSeats };
};

/** The count a local vote chose: '-2' is RUN IT 1X on the wire. 0 = no vote. */
const chosenRuns = (vote?: string): number => {
  if (vote === '-2') return 1;
  const runs = Number(vote);
  return runs === 2 || runs === 3 ? runs : 0;
};

/**
 * EN-10922: reports the multi-run vote as the local player lives it — see
 * multiRunVoteAnalytics for the events and what they are for.
 *
 * Mounted beside the action slot, like VoteDeliveryWatch, because the vote
 * panel unmounts the moment a vote is cast: the watch has to outlive it to see
 * the answer land and the hand resolve. It renders nothing and reads only what
 * the table already receives; every step is keyed to the vote window, so the
 * table's other mounted copies report nothing twice.
 */
const VoteAnalyticsWatch = () => {
  const game = useTableSelector((table) => table.game) as Game;
  const players = useTableSelector((table) => table.players);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const { playerMultiRunVote } = useContext(GameContext);
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const maxRunCount = useMaxRunCount();

  const promptRef = useRef<Prompt | null>(null);
  const handRef = useRef<HandWatch | null>(null);

  useEffect(() => {
    // Nothing to attribute until the player's GUID is known. The effect runs on
    // every frame, so a vote seen before hydration is picked up on the next.
    if (!playerGUID) return;
    const now = Date.now();
    const gameID = String(game?.gameID ?? '');
    const phase = readVotePhase(game);
    const open = isVoteOpen(phase);
    const counts = offeredRunCounts(game, seatId, maxRunCount);
    const facts = handFacts(game, players as Record<string, unknown> | undefined);
    const anchor = `${game?.time}|${game?.lastmove}`;

    // The initiator's client follows the whole window, for the per-hand result.
    if (open && isInitiatorSeat(game, seatId)) {
      if (!handRef.current) {
        handRef.current = { key: `${gameID}:${game?.lastmove}`, offered: 0, facts };
      }
      if (phase.phase === 'offered') handRef.current.offered = phase.offer;
    }

    // Asked.
    if (counts.length > 0 && !promptRef.current) {
      const role: MultiRunVoteRole = parseOffer(game?.runs) === 0 ? 'initiator' : 'responder';
      const offeredMax = Math.max(...counts);
      promptRef.current = {
        key: `${gameID}:${game?.lastmove}:${seatId}:${role}`,
        role,
        facts,
        offeredMax,
        shownAt: now,
        endAt: windowEndAt(game, now),
        anchor,
        cast: false
      };
      reportMultiRunVoteEventOnce(
        `${promptRef.current.key}:shown`,
        {
          name: 'multi_run_vote_prompt_shown',
          role,
          offeredMax,
          ...facts
        },
        playerGUID
      );
    }

    const prompt = promptRef.current;

    // Still asked: a frame that moved the clock re-anchors the deadline.
    if (prompt && counts.length > 0 && prompt.anchor !== anchor) {
      prompt.anchor = anchor;
      prompt.endAt = windowEndAt(game, now);
    }

    // Answered.
    const chosen = chosenRuns(playerMultiRunVote);
    if (prompt && !prompt.cast && chosen > 0) {
      prompt.cast = true;
      if (prompt.role === 'initiator' && chosen === 1 && handRef.current) {
        handRef.current.offered = 1;
      }
      reportMultiRunVoteEventOnce(
        `${prompt.key}:cast`,
        {
          name: 'multi_run_vote_cast',
          role: prompt.role,
          offeredMax: prompt.offeredMax,
          chosen,
          secondsToDecide: Math.round((now - prompt.shownAt) / 100) / 10,
          ...prompt.facts
        },
        playerGUID
      );
    }

    // No longer asked. Unanswered at the deadline is a timeout; unanswered
    // before it means the table closed the window, which `resolved` records.
    if (prompt && counts.length === 0) {
      if (!prompt.cast && now >= prompt.endAt - VOTE_TIMEOUT_TOLERANCE_MS) {
        reportMultiRunVoteEventOnce(
          `${prompt.key}:timeout`,
          {
            name: 'multi_run_vote_timeout',
            role: prompt.role,
            offeredMax: prompt.offeredMax,
            ...prompt.facts
          },
          playerGUID
        );
      }
      promptRef.current = null;
    }

    // Resolved. The processor turns `runs` from the vote straight into either
    // the run-out ('<n>,<hand>,1', the boards actually dealt) or '-2' (once).
    if (!open && handRef.current) {
      reportMultiRunVoteEventOnce(
        `${handRef.current.key}:resolved`,
        {
          name: 'multi_run_vote_resolved',
          offered: handRef.current.offered,
          resolvedRuns: phase.phase === 'running' ? phase.offer : 1,
          ...handRef.current.facts
        },
        playerGUID
      );
      handRef.current = null;
    }
  });

  return null;
};

export default VoteAnalyticsWatch;
